import { createElement, type ReactNode } from "react";

/** Where a search hit was found, and the text that shows why. */
export interface Snippet {
  matchedIn: "title" | "body";
  text: string;
}

/** Characters of surrounding text kept on each side of a body match. */
const CONTEXT_RADIUS = 60;

/**
 * The excerpt that shows why a page matched a search term.
 *
 * `DocsRepository.search` returns which pages matched, not where — it
 * checks the title and the whole body in one pass (see the adapter) and has
 * nothing more specific to report. This re-runs the same case-insensitive
 * check against content already in hand to find *where*, so "Filing
 * GSTR-1" showing up for "IGST" reads as obviously correct rather than a
 * mysterious hit.
 */
export function buildSnippet(
  term: string,
  title: string,
  body: string,
): Snippet {
  const needle = term.trim().toLowerCase();
  if (needle === "") return { matchedIn: "title", text: title };

  if (title.toLowerCase().includes(needle)) {
    return { matchedIn: "title", text: title };
  }

  const lowerBody = body.toLowerCase();
  const index = lowerBody.indexOf(needle);
  if (index === -1) {
    // The repository already decided this page matches; if this excerpt
    // cannot find where, something about its own matching (case folding,
    // whitespace) disagrees with the repository's — surface the opening of
    // the body rather than an empty cell, which would look like a bug in
    // this screen rather than a note about the mismatch.
    return {
      matchedIn: "body",
      text: body
        .slice(0, CONTEXT_RADIUS * 2)
        .replace(/\s+/g, " ")
        .trim(),
    };
  }

  const start = Math.max(0, index - CONTEXT_RADIUS);
  const end = Math.min(body.length, index + needle.length + CONTEXT_RADIUS);
  const prefix = start > 0 ? "…" : "";
  const suffix = end < body.length ? "…" : "";
  const excerpt = body.slice(start, end).replace(/\s+/g, " ").trim();
  return { matchedIn: "body", text: `${prefix}${excerpt}${suffix}` };
}

/** `text` with the search term set off from its surrounding words. */
export function highlightSnippet(text: string, term: string): ReactNode {
  const needle = term.trim();
  if (needle === "") return text;

  const index = text.toLowerCase().indexOf(needle.toLowerCase());
  if (index === -1) return text;

  return createElement(
    "span",
    null,
    text.slice(0, index),
    createElement(
      "mark",
      { className: "bg-accent text-inherit rounded-sm px-0.5" },
      text.slice(index, index + needle.length),
    ),
    text.slice(index + needle.length),
  );
}
